import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Fade,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  FETCH_CLIENT_ASSET_CATEGORIES,
  FETCH_CLIENT_ASSET_SUB_CATEGORIES,
  FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES,
  FETCH_COMPANIES_2,
  SAVE_CLIENT_FIXED_ASSETS,
  SAVE_CLIENT_FIXED_ASSET_PICTURES,
} from "../../constant/Apipath";
import { showErrorToast, showSuccessToast } from "../../toast/toast";
import { getFallbackFormFields } from "./constant/formFields";
import DynamicFormField from "./components/DynamicFormField";

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      color: "#94a3b8",
      mb: 2,
      mt: 1,
    }}
  >
    {children}
  </Typography>
);

const AddFixedAssetModal = ({ open, onClose, onRefresh }) => {
  const { loginData, selectedClientData } = useSelector(
    (state) => state?.main || {}
  );
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState({});
  const [dropdownLoadingMore, setDropdownLoadingMore] = useState({});
  const [dropdownPage, setDropdownPage] = useState({});
  const [dropdownHasMore, setDropdownHasMore] = useState({});
  const [vendorSearchText, setVendorSearchText] = useState("");
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorHasMore, setVendorHasMore] = useState(true);
  const [vendorTotalRecords, setVendorTotalRecords] = useState(0);
  const [vendorLoadingMore, setVendorLoadingMore] = useState(false);
  const [images, setImages] = useState([]);

  const accent = selectedClientData?.AccentColor || "#6366f1";

  const fetchFormFields = useCallback(async () => {
    setLoadingFields(true);
    try {
      const res = await Postrequestcall(
        "Client/FetchClientFormFieldSettings2",
        {
          PageNr: 1,
          NrOfRecPerPage: 20,
          FullSearch: "",
          UserId: loginData?.userInformation?.UserId || 1,
          IncludeRecordNr: "true",
          TypeOfObjectReturned: "",
          SearchList: [
            {
              ClientId: selectedClientData?.ClientId,
              appId: 21,
              AppFormKey: "ADD_ASSET",
              // formName: "AddAsset",
              isVisible: true,
            },
          ],
          SortList: [{}],
        },
        loginData?.accessToken
      );

      const fields =
        res?.status === 200 && res?.data?.data?.length > 0
          ? res.data.data
          : getFallbackFormFields("AddAsset");

      setFormFields(fields);

      const initialData = {};
      fields.forEach((f) => {
        initialData[f.fieldKey] = "";
      });
      setFormData(initialData);

      if (fields.find((f) => f.fieldKey === "CATEGORY"))
        fetchDropdownData(FETCH_CLIENT_ASSET_CATEGORIES, "CATEGORY");
      if (fields.find((f) => f.fieldKey === "STATUS"))
        fetchDropdownData(FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES, "STATUS");
    } catch (e) {
      const fallback = getFallbackFormFields("AddAsset");
      setFormFields(fallback);
    } finally {
      setLoadingFields(false);
    }
  }, [open, selectedClientData, loginData]);

  useEffect(() => {
    if (open) {
      fetchFormFields();
      setImages([]);
      setDropdownOptions({});
      setDropdownLoadingMore({});
      setDropdownPage({});
      setDropdownHasMore({});
      setVendorSearchText("");
      setVendorPage(1);
      setVendorHasMore(true);
      setVendorTotalRecords(0);
      setVendorLoadingMore(false);
      setDropdownOptions((prev) => ({ ...prev, VENDOR: [] }));
    }
  }, [open]);

  const fetchDropdownData = async (
    url,
    fieldKey,
    extraSearch = {},
    page = 1,
    append = false
  ) => {
    const pageSize = 20;
    if (append) {
      setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: true }));
    }
    setDropdownLoading((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      const payload = {
        PageNr: page,
        NrOfRecPerPage: pageSize, 
        FullSearch: "",
        SearchList: [{ ClientId: selectedClientData?.ClientId, ...extraSearch }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true,
      };
      const res = await Postrequestcall(url, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const items = res.data.data || [];
        const data = items.map((item) => ({
          id: item.id,
          label: item.name,
        }));
        const totalRecords = Number(res?.data?.nrOfRecords ?? res?.data?.RecordNr ?? 0);
        setDropdownOptions((prev) => ({
          ...prev,
          [fieldKey]: append ? [...(prev[fieldKey] || []), ...data] : data,
        }));
        setDropdownPage((prev) => ({ ...prev, [fieldKey]: page }));
        setDropdownHasMore((prev) => ({
          ...prev,
          [fieldKey]: totalRecords > 0 ? page * pageSize < totalRecords : items.length === pageSize,
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, [fieldKey]: false }));
      if (append) {
        setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: false }));
      }
    }
  };

  const fetchVendors = async (searchText = "", page = 1, append = false) => {
    const pageSize = 20;
    if (append) {
      setVendorLoadingMore(true);
    }
    setDropdownLoading((prev) => ({ ...prev, VENDOR: true }));
    try {
      const payload = {
        UserId: loginData?.userInformation?.UserId,
        SearchString: searchText,
        PageNumber: page,
        PageSize: pageSize,
        Manufacturer: true,
        Id: 0,
      };
      const res = await Postrequestcall(
        FETCH_COMPANIES_2,
        payload,
        loginData?.accessToken
      );
      if (res?.status === 201) {
        const vendors = res.data.data || [];
        const mappedVendors = vendors.map((v) => ({ id: v.id, label: v.name }));
        const totalRecords = Number(res?.data?.nrOfRecords ?? res?.data?.RecordNr ?? 0);
        setDropdownOptions((prev) => ({
          ...prev,
          VENDOR: append ? [...(prev.VENDOR || []), ...mappedVendors] : mappedVendors,
        }));
        setVendorPage(page);
        setVendorTotalRecords(totalRecords);
        setVendorHasMore(
          totalRecords > 0 ? page * pageSize < totalRecords : vendors.length === pageSize
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, VENDOR: false }));
      if (append) {
        setVendorLoadingMore(false);
      }
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    const dropdownFieldKeys = ["CATEGORY", "SUB_CATEGORY", "STATUS", "VENDOR"];

    if (dropdownFieldKeys.includes(fieldKey) && typeof value === "string") {
      return;
    }

    if (fieldKey === "SUB_CATEGORY") {
      const categoryFieldExists = formFields.some(
        (field) => field.fieldKey === "CATEGORY"
      );
      const hasCategory = !!(
        formData.CATEGORY &&
        ((typeof formData.CATEGORY === "object" && formData.CATEGORY.id) ||
          (typeof formData.CATEGORY === "string" &&
            formData.CATEGORY.trim() !== "") ||
          typeof formData.CATEGORY === "number")
      );

      if (categoryFieldExists && !hasCategory) {
        showErrorToast("Please select Category first");
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [fieldKey]: value }));

    if (fieldKey === "CATEGORY" && value) {
      const selectedCat = dropdownOptions.CATEGORY?.find(
        (c) => c.label === value || c.id === value
      );
      if (selectedCat) {
        setDropdownOptions((prev) => ({ ...prev, SUB_CATEGORY: [] }));
        setDropdownPage((prev) => ({ ...prev, SUB_CATEGORY: 1 }));
        setDropdownHasMore((prev) => ({ ...prev, SUB_CATEGORY: true }));
        fetchDropdownData(FETCH_CLIENT_ASSET_SUB_CATEGORIES, "SUB_CATEGORY", {
          AssetCategoryId: selectedCat.id,
        }, 1, false);
      }
    }
  };

  const handleDropdownLoadMore = (fieldKey) => {
    if (dropdownLoading[fieldKey] || !dropdownHasMore[fieldKey]) return;

    const api =
      fieldKey === "CATEGORY"
        ? FETCH_CLIENT_ASSET_CATEGORIES
        : fieldKey === "SUB_CATEGORY"
          ? FETCH_CLIENT_ASSET_SUB_CATEGORIES
          : fieldKey === "STATUS"
            ? FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES
            : null;

    if (!api) return;

    const nextPage = (dropdownPage[fieldKey] || 1) + 1;
    const extraSearch =
      fieldKey === "SUB_CATEGORY"
        ? {
            AssetCategoryId:
              dropdownOptions.CATEGORY?.find((c) => c.label === formData.CATEGORY || c.id === formData.CATEGORY)
                ?.id,
          }
        : {};

    fetchDropdownData(api, fieldKey, extraSearch, nextPage, true);
  };

  const handleVendorLoadMore = () => {
    if (dropdownLoading.VENDOR || !vendorHasMore) return;
    fetchVendors(vendorSearchText, vendorPage + 1, true);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || [])
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...mapped]);
  };

  const validateForm = () => {
    for (const field of formFields) {
      if (field.isVisible && field.isRequired && field.fieldKey !== "ADD_IMAGES" && !formData[field.fieldKey]) {
        showErrorToast(`${field.fieldName} is required`);
        return false;
      }
      if(field.fieldKey === "ADD_IMAGES" && field.isVisible && field.isRequired && images.length === 0) {
        showErrorToast(`${field.fieldName} is required`);
        return false;
      }
    }
    return true;
  };

  const resolveDropdownId = (value) => {
    if (value && typeof value === "object" && value.id !== undefined) {
      return value.id;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    return 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      console.log('formData', JSON.stringify(formData, null, 2))
      const saveData = {
        Id: 0,
        AssetCode: formData.ASSET_CODE || "",
        Asset: formData.ASSET_NAME || "",
        AssetStatusId: resolveDropdownId(formData.STATUS),
        AssetCategoryId: resolveDropdownId(formData.CATEGORY),
        AssetSubCategoryId: resolveDropdownId(formData.SUB_CATEGORY),
        BarCode: formData.BARCODE || "",
        SerialNo: formData.SERIAL_NUMBER || "",
        Make: formData.PRODUCT_NAME || "",
        Model: formData.MODEL || "",
        VendorId: resolveDropdownId(formData.VENDOR),
        PurchasedDate: formData.PURCHASE_DATE
          ? new Date(formData.PURCHASE_DATE).toISOString()
          : null,
        Price: parseFloat(formData.PRICE) || 0,
        WarrantyExpireDate: formData.WARRANTY_EXPIRY_DATE
          ? new Date(formData.WARRANTY_EXPIRY_DATE).toISOString()
          : null,
        Comments: formData.COMMENTS || "",
        ClientId: selectedClientData?.ClientId,
        ModifiedBy: loginData?.userInformation?.UserId,
      };

      const payload = {
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [saveData],
        UserId: loginData?.userInformation?.UserId,
      };
      const res = await Postrequestcall(
        SAVE_CLIENT_FIXED_ASSETS,
        payload,
        loginData?.accessToken
      );

      if (res?.status === 200 || res?.status === 201) {
        const assetId =
          res?.data?.data?.[0]?.objData?.id || res?.data?.RecordId;
        if (images.length > 0 && assetId) {
          for (const img of images) {
            const imgData = new FormData();
            imgData.append("Id", 0);
            imgData.append("AssetId", assetId);
            imgData.append("PictureUrl", img.file);
            imgData.append("PictureTypeId", 1);
            imgData.append("ClientId", selectedClientData?.ClientId);
            imgData.append("ModifiedBy", loginData?.userInformation?.UserId);
            imgData.append("UserId", loginData?.userInformation?.UserId);
            await Postrequestcall(
              SAVE_CLIENT_FIXED_ASSET_PICTURES,
              imgData,
              loginData?.accessToken,
              true
            );
          }
        }
        showSuccessToast("Asset created successfully");
        onRefresh();
        onClose();
      } else {
        throw new Error(res?.data?.message || "Failed to save asset");
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const visibleFields = formFields
    .filter((f) => f.isVisible !== false)
    .sort(
      (a, b) =>
        (parseInt(a.displayOrder) || 0) - (parseInt(b.displayOrder) || 0)
    );

  const imageField = visibleFields.find((f) => f.fieldKey === "ADD_IMAGES");
  const regularFields = visibleFields.filter(
    (f) => f.fieldKey !== "ADD_IMAGES"
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 250 }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0 25px 50px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 0,
          background: `linear-gradient(135deg, ${accent}18 0%, ${accent}06 100%)`,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${accent}44`,
              }}
            >
              <InventoryIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}
              >
                Add Fixed Asset
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Fill in the details below to register a new asset
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#64748b",
              bgcolor: "#f1f5f9",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#e2e8f0", color: "#1e293b" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: "#f8fafc",
          overflowY: "auto",
          maxHeight: "72vh",
        }}
      >
        {loadingFields ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 8,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: accent }} size={36} thickness={3} />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Loading form fields…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {/* Main fields */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                p: 3,
                mb: 2,
              }}
            >
              <SectionLabel>Asset Details</SectionLabel>
              <Grid container spacing={2.5}>
                {regularFields.map((field) => (
                  <Grid
                    size={{
                      xs: 12,
                      sm: field.fieldType === "MultilineText" ? 12 : 6,
                      md: field.fieldType === "MultilineText" ? 12 : 4,
                    }}
                    key={field.id || field.fieldKey}
                  >
                    <DynamicFormField
                      field={field}
                      value={formData[field.fieldKey]}
                      onChange={(val) => handleFieldChange(field.fieldKey, val)}
                      options={dropdownOptions[field.fieldKey] || []}
                      loading={false}
                      loadingMore={false}
                      onSearch={
                        field.fieldKey === "VENDOR"
                          ? (searchText) => {
                              setVendorSearchText(searchText);
                              setVendorPage(1);
                              setVendorHasMore(true);
                              setVendorTotalRecords(0);
                              setVendorLoadingMore(false);
                              setDropdownOptions((prev) => ({
                                ...prev,
                                VENDOR: [],
                              }));
                              fetchVendors(searchText, 1, false);
                            }
                          : undefined
                      }
                        onLoadMore={
                          field.fieldKey === "VENDOR"
                            ? handleVendorLoadMore
                            : ["CATEGORY", "SUB_CATEGORY", "STATUS"].includes(field.fieldKey)
                              ? () => handleDropdownLoadMore(field.fieldKey)
                              : undefined
                        }
                        hasMore={
                          field.fieldKey === "VENDOR"
                            ? vendorHasMore
                            : ["CATEGORY", "SUB_CATEGORY", "STATUS"].includes(field.fieldKey)
                              ? dropdownHasMore[field.fieldKey] || false
                              : false
                        }
                      onSelect={(val) => handleFieldChange(field.fieldKey, val)}
                      accentColor={accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Image Upload section */}
            {imageField && (
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  p: 3,
                }}
              >
                <SectionLabel>Photos</SectionLabel>
                
                <DynamicFormField
                  field={imageField}
                  value={formData[imageField.fieldKey]}
                  onChange={(val) =>
                    handleFieldChange(imageField.fieldKey, val)
                  }
                  accentColor={accent}
                  uploadedImages={images}
                  onImageAdd={handleImageChange}
                  onImageRemove={(i) =>
                    setImages((prev) => prev.filter((_, idx) => idx !== i))
                  }
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #f1f5f9",
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {visibleFields.filter((f) => f.isRequired).length > 0 && (
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              * Required fields
            </Typography>
          )}
        </Box>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: "9px",
            borderColor: "#e2e8f0",
            color: "#64748b",
            fontWeight: 500,
            "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "9px",
            fontWeight: 600,
            boxShadow: `0 4px 12px ${accent}44`,
            bgcolor: accent,
            "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            "&:disabled": { opacity: 0.7 },
            minWidth: 130,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" thickness={3} />
              Saving…
            </Box>
          ) : (
            "Save Asset"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFixedAssetModal;